import React, { useRef, useContext, useEffect } from "react";

import ThemeContext from "~/contexts/ThemeContext";

import styles from "./CodeInput.module.css";

type CodeInputProps = {
  length: number;
  id: string;
  name: string;
  values: string[];
  setValues: (x: string[]) => void;
};

const CodeInput: React.FC<CodeInputProps> = ({
  length,
  id,
  name,
  values,
  setValues,
}) => {
  const { template } = useContext(ThemeContext);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Autofocus on first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const focusInput = (index: number) => {
    if (index >= 0 && index < length) {
      const input = inputRefs.current[index];
      if (input) {
        input.focus();
        // Move cursor to end so text is not selected
        const len = input.value.length;
        input.setSelectionRange(len, len);
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const val = e.target.value.replace(/\D/g, "");

    if (val.length === 0) return;

    // If pasted/autofilled multiple digits into one field
    if (val.length > 1) {
      // Take only the new digits (exclude existing value)
      const newDigit =
        val.replace(values[index] || "", "").slice(0, 1) || val.slice(-1);
      const newValues = [...values];

      // If it's a real paste (more than 2 chars), distribute across fields
      if (val.length > 2) {
        const digits = val.slice(0, length - index).split("");
        digits.forEach((digit, i) => {
          if (index + i < length) {
            newValues[index + i] = digit;
          }
        });
        setValues(newValues);
        focusInput(Math.min(index + digits.length, length - 1));
        return;
      }

      // Otherwise just replace current cell with the new digit
      newValues[index] = newDigit;
      setValues(newValues);
      if (index < length - 1) {
        focusInput(index + 1);
      }
      return;
    }

    const newValues = [...values];
    newValues[index] = val;
    setValues(newValues);

    // Move to next input
    if (index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newValues = [...values];
      if (values[index]) {
        // Clear current cell
        newValues[index] = "";
        setValues(newValues);
      } else if (index > 0) {
        // Move to previous cell and clear it
        newValues[index - 1] = "";
        setValues(newValues);
        focusInput(index - 1);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;

    const digits = pasted.slice(0, length - index).split("");
    const newValues = [...values];
    digits.forEach((digit, i) => {
      if (index + i < length) {
        newValues[index + i] = digit;
      }
    });
    setValues(newValues);
    focusInput(Math.min(index + digits.length, length - 1));
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Don't select text — move cursor to end
    const len = e.target.value.length;
    e.target.setSelectionRange(len, len);
  };

  // Styles
  const variableStyles = {
    "--input-background-colour": template.global.input.backgroundColour,
    "--input-border-colour": template.global.input.borderColour,
    "--input-font-colour": template.global.input.fontColour,
    "--focus-input-background-colour":
      template.global.input.focusBackgroundColour,
    "--focus-input-border-colour": template.global.input.focusBorderColour,
  } as React.CSSProperties;

  return (
    <div className={styles.wrapper} style={variableStyles}>
      <div className={styles.cellContainer}>
        {Array.from({ length }).map((_, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={2}
            value={values[i] || ""}
            onChange={(e) => handleChange(e, i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            onPaste={(e) => handlePaste(e, i)}
            onFocus={handleFocus}
            className={`${styles.codeCell} ${
              values[i] ? styles.codeCellFilled : ""
            }`}
          />
        ))}
      </div>
      <input type="hidden" id={id} name={name} value={values.join("")} />
    </div>
  );
};

export default CodeInput;
